/**
 * Sprint 2026-06 — Cuerpo de tabla de items agrupados para vistas públicas
 * (Invoice y Estimate). Paridad de contenido con el PDF (pdf.py):
 * header por producto + meta (Método/Posición/Colores) + Nota + SKU y
 * sub-filas por variante (color · talla — qty — subtotal).
 * Solo presentación: nunca muestra campos vacíos ni datos internos.
 */
import React from "react";
import { groupItemsByProduct } from "@/lib/groupItems";

const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const itemName = (it) =>
  (it.is_custom && it.product_name) || it.description || it.product_code || "";
const colorSize = (it) =>
  [it.catalog_color, it.catalog_size].filter(Boolean).join(" · ");
const metaLine = (it) =>
  [
    it.decoration_method && `Método: ${it.decoration_method}`,
    it.decoration_position && `Posición: ${it.decoration_position}`,
    it.colors_used && `Colores: ${it.colors_used}`,
  ]
    .filter(Boolean)
    .join(" · ");

export function PublicGroupedItemsBody({ items, testidPrefix }) {
  return (
    <tbody>
      {groupItemsByProduct(items || []).map((g, gi) => {
        const head = g.variants[0];
        const multi = g.variants.length > 1;
        const meta = metaLine(head);
        const suffix = !multi ? colorSize(head) : "";
        return (
          <React.Fragment key={g.key || gi}>
            <tr
              data-testid={`${testidPrefix}-item-${gi}`}
              className="border-b border-neutral-200 align-top"
            >
              <td className="py-2.5 pr-2">
                <div className="font-medium">
                  {itemName(head)}
                  {suffix && (
                    <span className="text-neutral-600 font-normal"> ({suffix})</span>
                  )}
                </div>
                {meta && (
                  <div className="text-xs italic text-neutral-500 mt-0.5">{meta}</div>
                )}
                {head.customer_notes && (
                  <div className="text-xs italic text-neutral-500 mt-0.5">
                    Nota: {head.customer_notes}
                  </div>
                )}
                {head.sku && (
                  <div className="text-xs italic text-neutral-500 mt-0.5">
                    SKU: {head.sku}
                  </div>
                )}
              </td>
              <td className="py-2.5 px-2 text-right font-mono">{g.totalQty}</td>
              <td className="py-2.5 px-2 text-right font-mono">
                {money(head.unit_price)}
              </td>
              <td className="py-2.5 pl-2 text-right font-mono font-bold">
                {money(g.totalAmount)}
              </td>
            </tr>
            {multi &&
              g.variants.map((v, vi) => (
                <tr
                  key={vi}
                  data-testid={`${testidPrefix}-item-${gi}-variant-${vi}`}
                  className="border-b border-neutral-100 align-top"
                >
                  <td className="py-1.5 pr-2 pl-4 text-xs text-neutral-600">
                    {colorSize(v) || v.description || `Variante ${vi + 1}`}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono text-xs text-neutral-600">
                    {v.quantity}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono text-xs text-neutral-600">
                    {money(v.unit_price)}
                  </td>
                  <td className="py-1.5 pl-2 text-right font-mono text-xs text-neutral-600">
                    {money(v.subtotal != null ? v.subtotal : v.line_total)}
                  </td>
                </tr>
              ))}
          </React.Fragment>
        );
      })}
    </tbody>
  );
}
